import { useEffect, useState, useCallback } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDate } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import { listCoupons, createCoupon, updateCoupon, deleteCoupon } from "../../lib/adminApi";

const PLAN_OPTIONS = [
  ["starter", "Starter"],
  ["growth", "Growth"],
];

const rupees = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

function CouponsInner() {
  const { toast, openModal, closeModal } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listCoupons();
      setItems(d?.items || []);
    } catch (e) {
      toast(e.message || "Failed to load coupons", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openForm = (c) => {
    // Fixed discount + minAmount are stored in paise; edit in rupees.
    const draft = {
      code: c?.code || "",
      description: c?.description || "",
      discountType: c?.discountType || "percent",
      discountValue: c ? (c.discountType === "fixed" ? c.discountValue / 100 : c.discountValue) : 10,
      maxRedemptions: c?.maxRedemptions ?? "",
      minAmount: c?.minAmount != null ? c.minAmount / 100 : "",
      appliesToPlans: Array.isArray(c?.appliesToPlans) ? [...c.appliesToPlans] : [],
      expiresAt: c?.expiresAt ? String(c.expiresAt).slice(0, 10) : "",
      active: c ? c.active : true,
    };
    const set = (k, v) => { draft[k] = v; };
    const togglePlan = (key, checked) => {
      draft.appliesToPlans = checked
        ? [...new Set([...draft.appliesToPlans, key])]
        : draft.appliesToPlans.filter((p) => p !== key);
    };

    const save = async () => {
      if (!draft.code.trim()) return toast("Code is required", "error");
      const val = Number(draft.discountValue);
      if (!Number.isFinite(val) || val <= 0) return toast("Enter a valid discount value", "error");
      if (draft.discountType === "percent" && val > 100) return toast("Percentage cannot exceed 100", "error");
      const payload = {
        code: draft.code,
        description: draft.description || null,
        discountType: draft.discountType,
        discountValue: draft.discountType === "fixed" ? Math.round(val * 100) : Math.round(val),
        maxRedemptions: draft.maxRedemptions === "" ? null : Number(draft.maxRedemptions),
        minAmount: draft.minAmount === "" ? null : Math.round(Number(draft.minAmount) * 100),
        appliesToPlans: draft.appliesToPlans.length ? draft.appliesToPlans : null,
        expiresAt: draft.expiresAt || null,
        active: draft.active,
      };
      try {
        if (c) await updateCoupon(c.id, payload);
        else await createCoupon(payload);
        toast(c ? "Coupon updated" : "Coupon created", "success");
        closeModal();
        load();
      } catch (e) {
        toast(e.message === "code_exists" ? "That code already exists" : (e.message || "Save failed"), "error");
      }
    };

    openModal(
      <>
        <div className="modal-head">
          <h3>{c ? "Edit coupon" : "New coupon"}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field">
              <label>Code *</label>
              <input className="input" defaultValue={draft.code} placeholder="WELCOME20"
                style={{ textTransform: "uppercase" }}
                onChange={(e) => set("code", e.target.value.toUpperCase())} />
            </div>
            <div className="field">
              <label>Discount type</label>
              <select className="input" defaultValue={draft.discountType} onChange={(e) => set("discountType", e.target.value)}>
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed amount (₹)</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field">
              <label>Discount value</label>
              <input className="input" type="number" min="1" defaultValue={draft.discountValue}
                onChange={(e) => set("discountValue", e.target.value)} />
            </div>
            <div className="field">
              <label>Min order (₹, optional)</label>
              <input className="input" type="number" min="0" defaultValue={draft.minAmount}
                onChange={(e) => set("minAmount", e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Description (optional)</label>
            <input className="input" defaultValue={draft.description}
              onChange={(e) => set("description", e.target.value)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field">
              <label>Max redemptions (blank = ∞)</label>
              <input className="input" type="number" min="1" defaultValue={draft.maxRedemptions}
                onChange={(e) => set("maxRedemptions", e.target.value)} />
            </div>
            <div className="field">
              <label>Expires (optional)</label>
              <input className="input" type="date" defaultValue={draft.expiresAt}
                onChange={(e) => set("expiresAt", e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Applies to plans (none = all)</label>
            <div style={{ display: "flex", gap: 16 }}>
              {PLAN_OPTIONS.map(([key, label]) => (
                <label key={key} className="checkbox" style={{ margin: 0 }}>
                  <input type="checkbox" defaultChecked={draft.appliesToPlans.includes(key)}
                    onChange={(e) => togglePlan(key, e.target.checked)} /> {label}
                </label>
              ))}
            </div>
          </div>
          <label className="checkbox" style={{ marginTop: 4 }}>
            <input type="checkbox" defaultChecked={draft.active} onChange={(e) => set("active", e.target.checked)} /> Active
          </label>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={save}><Icon name="check" size={14} /> {c ? "Save" : "Create"}</button>
        </div>
      </>,
      "lg",
    );
  };

  const confirmDelete = (c) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Delete coupon</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 14, color: "var(--text-muted)" }}>
            Delete <strong style={{ color: "var(--text)" }}>{c.code}</strong>? Existing redemptions are kept for records.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger" onClick={async () => {
            try { await deleteCoupon(c.id); toast("Coupon deleted", "success"); closeModal(); load(); }
            catch (e) { toast(e.message, "error"); }
          }}><Icon name="trash-2" size={14} /> Delete</button>
        </div>
      </>,
      "sm",
    );
  };

  const discountLabel = (c) =>
    c.discountType === "percent" ? `${c.discountValue}% off` : `${rupees(c.discountValue)} off`;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Coupons</h1>
          <p className="page-sub">{items.length} discount code{items.length === 1 ? "" : "s"}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openForm(null)}>
          <Icon name="plus" size={14} /> New coupon
        </button>
      </div>

      {loading ? (
        <TableSkeleton columns={6} rows={5} />
      ) : items.length === 0 ? (
        <EmptyState title="No coupons yet" desc="Create a discount code to get started." icon="tag" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Used</th>
                <th>Plans</th>
                <th>Expires</th>
                <th>Status</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td data-label="Code" style={{ fontWeight: 700, fontSize: 13, fontFamily: "monospace" }}>{c.code}</td>
                  <td data-label="Discount">{discountLabel(c)}</td>
                  <td data-label="Used" style={{ fontSize: 12.5 }}>
                    {c.redeemedCount}{c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ""}
                  </td>
                  <td data-label="Plans" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {Array.isArray(c.appliesToPlans) && c.appliesToPlans.length ? c.appliesToPlans.join(", ") : "All"}
                  </td>
                  <td data-label="Expires" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {c.expiresAt ? fmtDate(c.expiresAt) : "—"}
                  </td>
                  <td data-label="Status">
                    <span className={`badge ${c.active ? "badge-success" : "badge-muted"}`}>
                      <span className="badge-dot" /> {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td data-label="">
                    <button className="icon-btn" title="Edit" onClick={() => openForm(c)}><Icon name="pencil" size={14} /></button>
                    <button className="icon-btn" title="Delete" onClick={() => confirmDelete(c)}><Icon name="trash-2" size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminCoupons() {
  return (
    <AdminPageBoundary>
      <CouponsInner />
    </AdminPageBoundary>
  );
}
